import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRazorpay } from "react-razorpay";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Package, ShoppingCart, Loader2, QrCode, Copy, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { API, UPI_ID, WHATSAPP_NUMBER } from "@/config";
import { PRODUCTS } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

export const Products = ({ onEnquire }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { Razorpay } = useRazorpay();
  const [buying, setBuying] = useState(null);
  const [upiProduct, setUpiProduct] = useState(null);

  const buyViaRazorpay = async (product) => {
    setBuying(`rzp-${product.id}`);
    try {
      const { data } = await axios.post(
        `${API}/payments/razorpay/order`,
        { product_id: product.id },
        { withCredentials: true }
      );
      const rzp = new Razorpay({
        key: data.key_id,
        amount: data.amount_paise,
        currency: "INR",
        name: t.brandFull,
        description: t.productItems[product.id].name,
        order_id: data.order_id,
        theme: { color: "#E11D48" },
        modal: { ondismiss: () => setBuying(null) },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/razorpay/verify`, resp, { withCredentials: true });
            navigate("/payment/status?gateway=razorpay&result=success");
          } catch {
            toast.error(t.enquiry.error);
            setBuying(null);
          }
        },
      });
      rzp.open();
    } catch {
      toast.error(t.enquiry.error);
      setBuying(null);
    }
  };

  const copyUpiId = async () => {
    try {
      await navigator.clipboard.writeText(UPI_ID);
      toast.success(t.upi.copied);
    } catch {
      toast.error(t.enquiry.error);
    }
  };

  const buyNow = async (productId) => {
    setBuying(productId);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        product_id: productId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error(t.enquiry.error);
      setBuying(null);
    }
  };

  return (
    <section id="products" className="border-y border-border bg-muted/40">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-widest text-rose-600">
            {t.serviceItems["trading-wholesale-retail"].title}
          </p>
          <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            {t.products.heading}
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            {t.products.subheading}
          </p>
        </div>

        <div data-testid="products-section-container" className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p, i) => {
            const item = t.productItems[p.id];
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
                data-testid={`product-card-${p.id}`}
                className="group overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-xl"
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={p.image}
                    alt={item.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute right-3 top-3 rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white shadow">
                    {item.price}
                  </span>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-rose-600" />
                    <h3 className="font-heading text-base font-semibold">{item.name}</h3>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{item.desc}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      data-testid={`product-buy-button-${p.id}`}
                      disabled={buying === p.id}
                      className="gap-1.5"
                      onClick={() => buyNow(p.id)}
                    >
                      {buying === p.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <ShoppingCart className="h-3.5 w-3.5" />
                      )}
                      {t.products.buyNow} · ₹{p.priceInr}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`product-razorpay-button-${p.id}`}
                      disabled={buying === `rzp-${p.id}`}
                      className="gap-1.5"
                      onClick={() => buyViaRazorpay(p)}
                    >
                      {buying === `rzp-${p.id}` ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Wallet className="h-3.5 w-3.5" />
                      )}
                      {t.products.razorpay}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`product-upi-button-${p.id}`}
                      className="gap-1.5"
                      onClick={() => setUpiProduct(p)}
                    >
                      <QrCode className="h-3.5 w-3.5" />
                      {t.upi.payViaUpi}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`product-enquire-button-${p.id}`}
                      onClick={() => onEnquire(p.serviceId)}
                    >
                      {t.products.enquire}
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!upiProduct} onOpenChange={(o) => !o && setUpiProduct(null)}>
        <DialogContent data-testid="upi-payment-modal" className="max-w-sm text-center">
          {upiProduct && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-lg">
                  {t.upi.payViaUpi} — {t.productItems[upiProduct.id].name}
                </DialogTitle>
                <DialogDescription className="text-sm">{t.upi.scanQr}</DialogDescription>
              </DialogHeader>
              <img
                src="/upi-qr.png"
                alt="UPI QR Code"
                data-testid="upi-qr-image"
                className="mx-auto w-56 rounded-xl border border-border"
              />
              <p className="font-heading text-2xl font-extrabold">
                {t.upi.amount}: ₹{upiProduct.priceInr}
              </p>
              <div className="flex items-center justify-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2">
                <span data-testid="upi-id-text" className="text-sm font-semibold">{UPI_ID}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid="upi-copy-button"
                  onClick={copyUpiId}
                  className="gap-1"
                >
                  <Copy className="h-3.5 w-3.5" /> {t.upi.copy}
                </Button>
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">{t.upi.note}</p>
              <Button
                asChild
                variant="outline"
                data-testid="upi-whatsapp-confirm-button"
                className="gap-2 border-emerald-600/40 text-emerald-600 hover:bg-emerald-600/10"
              >
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Payment done via UPI for ${t.productItems[upiProduct.id].name} (₹${upiProduct.priceInr}). Screenshot attached.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp
                </a>
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
